import { NavigateOptions, useNavigate } from '@tanstack/react-router';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { getAuth } from '~/auth/getAuth';
import { logoutServer } from '~/auth/logout';

import { PublicAuthData } from '~/auth/session';
import { ToastKey } from '~/util/types/toasts';
import { AuthContext } from './AuthContext';

export { useAuth, useAuthData, useScopes } from './AuthContext';
const BUFFER_BEFORE_REFRESH = 20_000;

export const AuthProvider: React.FC<{
    initial: Partial<PublicAuthData> | null;
    children: React.ReactNode;
}> = ({ initial, children }) => {
    const [auth, setAuth] = useState(initial);
    const navigate = useNavigate();

    const timerId = useRef<number | null>(null);
    const inflight = useRef<Promise<PublicAuthData | null> | null>(null);

    const logout = useCallback(
        (
            showToast: ToastKey = 'logoutSuccess',
            redirectTo: NavigateOptions['to'] = '/',
        ) => {
            setAuth(null);
            if (timerId.current) clearTimeout(timerId.current);
            inflight.current = null;
            timerId.current = null;
            logoutServer({ data: {} });
            navigate({ to: redirectTo, search: { showToast } });
        },
        [navigate],
    );

    const refreshAuthData = useCallback(
        async function (): Promise<PublicAuthData | null> {
            if (inflight.current) return inflight.current;

            inflight.current = (async () => {
                try {
                    const new_auth = (await getAuth()) as PublicAuthData;
                    setAuth(new_auth);
                    inflight.current = null;
                    return new_auth;
                } catch (error) {
                    console.error('Failed to refresh auth data:', error);
                    logout('logoutSessionExpired');
                    return null;
                }
            })();

            return inflight.current;
        },
        [logout],
    );

    useEffect(() => {
        if (!auth?.access_expires) return;

        const expiration = new Date(auth.access_expires).getTime();
        const time_before_exp = Math.max(
            0,
            expiration - Date.now() - BUFFER_BEFORE_REFRESH,
        );

        timerId.current = window?.setTimeout(() => {
            void refreshAuthData();
        }, time_before_exp);

        return () => {
            if (timerId.current) clearTimeout(timerId.current);
        };
    }, [auth?.access_expires, refreshAuthData]);

    return (
        <AuthContext.Provider value={{ auth, logout, refreshAuthData }}>
            {children}
        </AuthContext.Provider>
    );
};
